<template>
  <q-page padding>
    <PageHeader>
      <template #title> Criação de Documentos </template>
      <template #actions>
        <q-icon
          style="cursor: pointer"
          name="info"
          size="xs"
          @click="informacaoUpload = true"
          class="q-ml-sm text-brand-1"
        />
      </template>
    </PageHeader>

    <div class="row items-center q-mt-md q-mb-md">
      <div class="col-md-4">
        <div class="text-weight-bold text-brand-1 q-mb-xs">Unidade</div>
        <ListaUnidadesSelectVue />
      </div>
    </div>

    <div class="upload-section">
      <div class="upload-header">
        <q-icon name="upload_file" size="24px" class="header-icon" />
        <h6 class="upload-title">Enviar arquivo de criação de documentos</h6>
      </div>
      <div class="upload-content">
        <div
          class="upload-area"
          :class="{ 'file-selected': file, 'drag-over': isDragOver }"
          @dragover.prevent="isDragOver = true"
          @dragleave.prevent="isDragOver = false"
          @drop.prevent="onDrop"
        >
          <div class="upload-zone">
            <div class="upload-icon-container">
              <q-icon name="cloud_upload" size="48px" class="upload-icon" />
            </div>
            <div class="upload-text-container">
              <p class="upload-main-text" v-if="!file">
                Arraste e solte seu arquivo aqui ou
              </p>
              <p class="upload-main-text selected" v-else>
                <q-icon name="description" class="q-mr-xs" />{{ file.name }}
              </p>
              <div class="upload-button">
                <q-btn
                  :color="file ? 'positive' : 'primary'"
                  :icon="file ? 'check_circle' : 'folder_open'"
                  :label="file ? 'Arquivo Selecionado' : 'Selecionar Arquivo'"
                  no-caps
                  rounded
                  class="upload-btn"
                  @click="fileInput.click()"
                />
                <input
                  ref="fileInput"
                  type="file"
                  accept=".csv"
                  @change="onFileSelected"
                  style="display: none"
                />
              </div>
            </div>
          </div>
          <div class="file-info" v-if="file">
            <div class="file-details">
              <q-chip color="primary" text-color="white" icon="info"
                >Tamanho: {{ formatFileSize(file.size) }}</q-chip
              >
              <q-chip color="positive" text-color="white" icon="event"
                >Tipo: {{ file.type || "CSV" }}</q-chip
              >
            </div>
          </div>
        </div>
        <div class="upload-actions">
          <q-btn
            color="positive"
            label="Enviar Planilha"
            no-caps
            icon="send"
            size="md"
            :disable="!file"
            @click="enviarArquivo"
            class="send-button"
          />
        </div>
      </div>
    </div>

    <q-dialog v-model="informacaoUpload">
      <q-card style="width: 80%">
        <div class="q-gutter-md">
          <q-carousel
            v-model="slide"
            transition-prev="scale"
            transition-next="scale"
            swipeable
            animated
            control-color="white"
            navigation
            padding
            arrows
            height="300px"
            class="bg-brand-1 text-white shadow-1 rounded-borders"
          >
            <q-carousel-slide :name="0" class="column no-wrap flex-center">
              <a
                download
                href="https://multpex-docs.s3.amazonaws.com/templates/gestao_de_documento/Criac%CC%A7a%CC%83o_Documentos.csv"
              >
                <q-icon name="file_download" size="56px" />
              </a>
              <div class="q-mt-md text-center">
                Clique no icone para baixar o modelo mais atualizado do template
              </div>
            </q-carousel-slide>
          </q-carousel>
        </div>
      </q-card>
    </q-dialog>
  </q-page>
</template>

<script setup>
import { useQuasar } from "quasar";
import { ref, watch, onMounted } from "vue";
import { useStore } from "vuex";

import ListaUnidadesSelectVue from "../../organizacional/unidade/component/ListaUnidadesSelect.vue";
import PageHeader from "../../../components/PageHeader.vue";

const store = useStore();
const $q = useQuasar();

const unidade = ref({
  nome: store.state.org.unidadeData,
  id: store.state.org.unidadeData.id,
});
const file = ref(null);
const fileInput = ref(null);
const isDragOver = ref(false);
const informacaoUpload = ref(false);
const slide = ref(0);

const formatFileSize = (bytes) => {
  if (!bytes) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const onFileSelected = (e) => {
  if (e.target.files[0]) file.value = e.target.files[0];
};

const onDrop = (e) => {
  isDragOver.value = false;
  const f = e.dataTransfer.files[0];
  if (f && f.name.endsWith(".csv")) {
    file.value = f;
  } else {
    $q.notify({
      message: "Por favor, selecione apenas arquivos .csv",
      color: "negative",
      icon: "warning",
    });
  }
};

const enviarArquivo = async () => {
  if (!store.state.org.unidadeData.id) {
    $q.notify({
      message: "Selecione uma unidade para fazer upload.",
      color: "negative",
      actions: [{ label: "Fechar", color: "white", handler: () => {} }],
    });
    return;
  }
  if (!file.value) {
    $q.notify({
      message: "Selecione um arquivo.",
      color: "negative",
      actions: [{ label: "Fechar", color: "white", handler: () => {} }],
    });
    return;
  }
  if (file.value.name.split(".").pop() !== "csv") {
    $q.notify({
      message: "O arquivo tem que ser do tipo .csv",
      color: "negative",
      actions: [{ label: "Fechar", color: "white", handler: () => {} }],
    });
    return;
  }

  $q.loading.show({ message: "Enviando arquivo..." });

  try {
    const response = await store.dispatch("documento/gerarUrlUpload");
    const formAttributes = response.data.resultado.dados.formAttributes;
    const formInputs = response.data.resultado.dados.formInputs;
    const formData = new FormData();

    for (const input in formInputs) {
      formData.append(input, formInputs[input]);
    }
    formData.append("file", file.value);

    const request = new XMLHttpRequest();
    request.open(formAttributes.method, formAttributes.action);
    request.send(formData);

    await store.dispatch("documento/fazerUploadArquivo", {
      url: `${formAttributes.action}/${formInputs.key}`,
      unidadeId: store.state.org.unidadeData.id,
    });

    $q.loading.hide();
    $q.notify({
      message: "Documento(s) adicionados com sucesso.",
      color: "positive",
      actions: [{ label: "Fechar", color: "white", handler: () => {} }],
    });
    file.value = null;
  } catch (error) {
    $q.loading.hide();
    $q.notify({
      message: "Erro ao fazer upload de documentos.",
      color: "negative",
      actions: [{ label: "Fechar", color: "white", handler: () => {} }],
    });
  }
};

watch(
  () => store.state.org.unidadeData,
  () => {
    unidade.value.nome = store.state.org.unidadeData;
    unidade.value.id = store.state.org.unidadeData.id;
  }
);

const getUnidade = () => {
  if (localStorage.unidade && localStorage.unidade !== "null") {
    unidade.value.nome = JSON.parse(localStorage.getItem("unidade"));
  }
};

onMounted(getUnidade);
</script>

<style scoped>
.upload-section {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border-radius: 16px;
  padding: 24px;
  margin: 16px 0;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.upload-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  gap: 12px;
}

.header-icon {
  color: #1976d2;
}

.upload-title {
  margin: 0;
  color: #2c3e50;
  font-weight: 600;
  font-size: 1.1rem;
}

.upload-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.upload-area {
  background: white;
  border: 2px dashed #e0e4e7;
  border-radius: 12px;
  padding: 32px;
  text-align: center;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.upload-area:hover {
  border-color: #1976d2;
  background: #f8fafe;
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(25, 118, 210, 0.1);
}

.upload-area.file-selected {
  border-color: #4caf50;
  background: #f1f8e9;
}

.upload-area.drag-over {
  border-color: #1976d2;
  background: #e3f2fd;
  transform: scale(1.02);
}

.upload-zone {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.upload-icon-container {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}

.upload-icon {
  color: white;
}

.upload-text-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.upload-main-text {
  margin: 0;
  color: #546e7a;
  font-size: 1rem;
  font-weight: 500;
}

.upload-main-text.selected {
  color: #2e7d32;
  font-weight: 600;
}

.upload-button {
  cursor: pointer;
}

.upload-btn {
  min-width: 180px;
  height: 44px;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;
}

.upload-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

.file-info {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e0e4e7;
}

.file-details {
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}

.upload-actions {
  display: flex;
  justify-content: center;
}

.send-button {
  min-width: 160px;
  height: 44px;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;
}

.send-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

a {
  text-decoration: none;
  color: white;
}
</style>
